'use client';

/**
 * 全局 Worker 详情弹窗
 */

import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import { getAgentDetail } from '@/services/thread';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Skeleton } from '@/components/ui/loading';
import { EVT_OPEN_WORKER_DETAIL } from '@/constants/event';
import AgentTokenConfigModal from '@/components/agent-token-config-modal';
import { useI18n } from '@/hooks/use-i18n';
import AgentInfoBar from '@/components/agent-info-bar';

export const openWorkerDetail = (
  agentKey: string,
  mcpStatus: 'missing_key' | 'failed' | 'success' | null = null,
  mcpCheck: boolean = false,
) => {
  window.dispatchEvent(
    new CustomEvent(EVT_OPEN_WORKER_DETAIL, {
      detail: {
        agentKey,
        mcpStatus,
        mcpCheck,
      },
    }),
  );
};

const WorkerDetailModal = () => {
  const { t } = useI18n('agent');
  const [agentKey, setAgentKey] = useState<string | null>(null);
  const [agentDetail, setAgentDetail] = useState<AGENT.Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [mcpStatus, setMcpStatus] = useState<
    'missing_key' | 'failed' | 'success' | null
  >(null);
  const [mcpCheck, setMcpCheck] = useState<boolean>(false);
  const [isShowAgentTokenConfigModal, setIsShowAgentTokenConfigModal] =
    useState(false);

  useEffect(() => {
    const handleAgentDetail = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (!customEvent.detail?.agentKey) {
        return;
      }

      setAgentKey(customEvent.detail.agentKey);
      setMcpStatus(customEvent.detail.mcpStatus || null);
      setMcpCheck(customEvent.detail.mcpCheck || false);
      console.log('customEvent.detail', customEvent.detail);
    };

    // Add event listener
    window.addEventListener(
      EVT_OPEN_WORKER_DETAIL,
      handleAgentDetail as EventListener,
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        EVT_OPEN_WORKER_DETAIL,
        handleAgentDetail as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (!agentKey) {
      return;
    }
    setLoading(true);
    getAgentDetail(agentKey)
      .then((res) => {
        setAgentDetail(res);
      })
      .catch((error) => {
        console.error('获取详情失败', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [agentKey]);

  return (
    <>
      <AgentTokenConfigModal
        isShow={isShowAgentTokenConfigModal}
        onClose={() => setIsShowAgentTokenConfigModal(false)}
        onCloseAll={() => {
          setIsShowAgentTokenConfigModal(false);
          setAgentKey(null);
          setAgentDetail(null);
        }}
        agentName={agentDetail?.cn_name || ''}
        agentKey={agentDetail?.agent_key || ''}
        type={mcpStatus === 'missing_key' ? 'configure' : 'manage'}
        mcpStatus={mcpStatus}
      />
      <Modal
        open={!!agentKey}
        onCancel={() => {
          setAgentKey(null);
          setAgentDetail(null);
        }}
        title={<></>}
        closeButtonYOffset={-64}
        headerHeight={40}
        width={880}
        height={664}
        footer={null}
        className="worker-detail-modal"
        classNames={{
          content: '!pb-[0]',
        }}
      >
        <div className="flex flex-col h-full justify-between">
          <div className="px-6 overflow-y-auto flex flex-col gap-4 flex-1">
            <div className="bg-[#F4F4F5] p-6 rounded-[12px] flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {agentDetail?.img_url && (
                  <Image
                    src={agentDetail.img_url}
                    alt={agentDetail?.cn_name || ''}
                    width={24}
                    height={24}
                    className="rounded-full ring-1 ring-gray-100 shadow-sm"
                  />
                )}
                <div className="text-[#000000BF] text-[16px] font-semibold flex-1">
                  {agentDetail?.cn_name}
                </div>

                {mcpCheck && (
                  <div
                    onClick={() => setIsShowAgentTokenConfigModal(true)}
                    className="font-[600] bg-[#e7e7f4] px-[8px] py-[5px] rounded-[9999px] text-[14px] text-[#7171EE] cursor-pointer transition-all duration-200 hover:bg-[#d5d5ef] hover:text-[#5454c5]"
                    style={{ transition: 'background 0.2s, color 0.2s' }}
                  >
                    {t('workerDetailModal.manageKeys')}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                {agentDetail?.tags?.map((tag) => (
                  <div
                    key={tag.name}
                    className="text-[#7171EE] bg-[#7171EE33] p-[2px_6px] rounded-[4px]"
                  >
                    {tag.name}
                  </div>
                ))}
              </div>
              {loading ? (
                // 骨架组件占位 - 描述信息
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : (
                <div className="text-[13px] text-[#00000073]">
                  {agentDetail?.description}
                </div>
              )}
            </div>
            {loading ? (
              // 骨架组件占位 - Agent 能力和常见问题
              <>
                <div>
                  <div className="text-[14px] text-[#00000073] font-semibold px-4 py-[10px]">
                    {t('workerDetailModal.agentCapabilities')}
                  </div>
                  <div className="flex flex-col gap-3">
                    {[1, 2].map((index) => (
                      <Skeleton
                        key={`tool-${index}`}
                        className="h-12 rounded-[12px] mb-[10px]"
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[14px] text-[#00000073] font-semibold px-4 py-[10px]">
                    {t('workerDetailModal.commonQuestions')}
                  </div>
                  <div className="flex flex-col gap-3">
                    {[1, 2, 3].map((index) => (
                      <Skeleton
                        key={`question-${index}`}
                        className="h-14 rounded-[12px]"
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                {!!agentDetail?.tools?.length && (
                  <div>
                    <div className="text-[14px] text-[#00000073] font-semibold px-4 py-[10px]">
                      {t('workerDetailModal.agentCapabilities')}
                    </div>
                    <div className="flex flex-col gap-3">
                      {agentDetail?.tools?.map((tool) => (
                        <AgentInfoBar
                          key={tool.worker_name}
                          name={tool.chinese_name}
                          icon={agentDetail?.img_url}
                          desc={tool.description}
                          className="mb-[10px]"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {!!agentDetail?.questions?.length && (
                  <div>
                    <div className="text-[14px] text-[#00000073] font-semibold px-4 py-[10px]">
                      {t('workerDetailModal.commonQuestions')}
                    </div>
                    <div className="flex flex-col gap-3">
                      {agentDetail?.questions?.map((question, index) => (
                        <div
                          key={question}
                          className="flex items-start gap-2 bg-white border-[1px] border-[#E5E5E5] px-4 py-[13px] rounded-[12px]"
                        >
                          <div className="text-[10px] bg-[#7171EE] text-white font-semibold w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            {index + 1}
                          </div>
                          <div className="text-[13px] text-black font-medium">
                            {question}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="text-xs text-[#00000040] flex items-center justify-center min-h-0 shrink justify-self-end px-4 py-[8px]">
            {agentDetail?.explain}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WorkerDetailModal;
